import { formatDataverseRecordKey } from '../lib/dataverseRecordId';
import { PROVIDER_OPTION_VALUES } from '../config/lockProviders';
import { listAllLocks } from './unlocKeyService';

const EXISTING_SELECT = 'oc_lockid,oc_unlocupdatedon';
const UNLOC_PROVIDER = PROVIDER_OPTION_VALUES.unloc;
const CONCURRENCY = 8;

function emptyToNull(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return value;
}

function buildBeskrivelse(lock) {
  const street = lock?.address?.street;
  const city = lock?.address?.city;
  if (!street && !city) {
    return lock?.name || null;
  }
  if (!street || !city) {
    return street || city;
  }
  return `${street}, ${city}`;
}

function locationSource(lock) {
  if (lock?.location?.latitude != null) {
    return 'location';
  }
  if (lock?.locationGuess?.latitude != null) {
    return 'locationGuess';
  }
  return null;
}

function uniqueNewcolumn(lock) {
  // Primary name column; keep stable & unique while some envs still have managed oc_pk key.
  const short = String(lock?.id || '').replace(/[{}]/g, '').slice(0, 8);
  const name = (lock?.name || 'Lås').trim().slice(0, 80);
  return short ? `${name} (${short})` : name;
}

function mapUnlocLockToDataverse(lock) {
  const now = new Date().toISOString();
  return {
    oc_name: emptyToNull(lock.name),
    oc_beskrivelse: buildBeskrivelse(lock),
    oc_newcolumn: uniqueNewcolumn(lock),
    oc_batterylevel: emptyToNull(lock.batteryStatus?.batteryLevel),
    oc_batterylevellastupdated: emptyToNull(lock.batteryStatus?.batteryLevelLastUpdated),
    oc_canbelocked: lock.canBeLocked ?? null,
    oc_city: emptyToNull(lock.address?.city),
    oc_countryiso: emptyToNull(lock.address?.countryIso),
    oc_duplicatelock: lock.duplicateLock ?? null,
    oc_duplicatesourcelockid: emptyToNull(lock.duplicateSourceLock?.id),
    oc_duplicatesourcelockname: emptyToNull(lock.duplicateSourceLock?.name),
    oc_floor: emptyToNull(lock.address?.floor),
    oc_imageurl: emptyToNull(lock.imageUrl),
    oc_lockconnectionid: emptyToNull(lock.lockConnectionId),
    oc_locationsource: locationSource(lock),
    oc_physicallockid: emptyToNull(lock.physicalLockId),
    oc_postalcode: emptyToNull(lock.address?.postalCode),
    oc_privacypolicy: emptyToNull(lock.privacyPolicy),
    oc_projectid: emptyToNull(lock.duplicateSourceLock?.project?.id),
    oc_projectname: emptyToNull(lock.duplicateSourceLock?.project?.name),
    oc_projectorganizationid: emptyToNull(lock.duplicateSourceLock?.project?.organizationId),
    oc_projectorganizationname: emptyToNull(lock.duplicateSourceLock?.project?.organizationName),
    oc_street: emptyToNull(lock.address?.street),
    oc_timezone: emptyToNull(lock.timezone),
    oc_unloccreatedon: emptyToNull(lock.created),
    oc_vendor: emptyToNull(lock.vendor),
    oc_vendortype: emptyToNull(lock.vendorType),
    oc_visitorinstructions: emptyToNull(lock.address?.visitorInstructions),
    oc_latitude: lock.location?.latitude ?? lock.locationGuess?.latitude ?? null,
    oc_longitude: lock.location?.longitude ?? lock.locationGuess?.longitude ?? null,
    oc_provider: UNLOC_PROVIDER,
    oc_externalid: lock.id,
    oc_lastsyncedon: now,
    oc_unlocupdatedon: emptyToNull(lock.updated),
    statecode: 0,
    statuscode: 1
  };
}

/**
 * Normalize Unloc/Dataverse datetimes for skip comparison.
 * Dataverse often returns TimeZoneIndependent values without "Z"; parse those as UTC.
 * Compare at second precision so ms truncation in Dataverse does not force re-upsert every run.
 */
function toUtcSeconds(value) {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const raw = String(value).trim();
  const hasTz = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const ms = Date.parse(hasTz ? raw : `${raw}Z`);
  if (Number.isNaN(ms)) {
    return null;
  }
  return Math.floor(ms / 1000);
}

function shouldSkip(lock, existingUpdatedOn) {
  const incoming = toUtcSeconds(lock?.updated);
  const stored = toUtcSeconds(existingUpdatedOn);
  if (incoming == null || stored == null) {
    return false;
  }
  return incoming <= stored;
}

async function mapPool(items, limit, worker) {
  const results = new Array(items.length);
  let next = 0;

  async function run() {
    while (next < items.length) {
      const index = next;
      next += 1;
      results[index] = await worker(items[index], index);
    }
  }

  const runners = Array.from({ length: Math.min(limit, items.length) || 1 }, () => run());
  await Promise.all(runners);
  return results;
}

async function listAllExistingUnlocLocks(dataverseApiCall) {
  const byId = new Map();
  let url =
    `/oc_locks?$select=${EXISTING_SELECT}` +
    `&$filter=oc_provider eq ${UNLOC_PROVIDER} and statecode eq 0` +
    `&$top=5000`;

  while (url) {
    const page = await dataverseApiCall(url);
    for (const row of page?.value || []) {
      const id = String(row.oc_lockid || '').toLowerCase();
      if (id) {
        byId.set(id, row.oc_unlocupdatedon || null);
      }
    }
    url = page?.['@odata.nextLink'] || null;
  }

  return byId;
}

function isMissingRecordError(err) {
  const message = String(err?.message || '');
  return (
    message.includes('404') ||
    message.includes('412') ||
    /does not exist|not found|precondition/i.test(message)
  );
}

function isUniqueConstraintError(err) {
  const message = String(err?.message || '');
  return /duplicate|unique|oc_newcolumn|already exists/i.test(message);
}

function withUniqueNewcolumn(body, lockId) {
  const short = String(lockId || '').replace(/[{}]/g, '').slice(0, 8);
  const base = (body.oc_name || body.oc_newcolumn || 'Lås').slice(0, 80);
  return { ...body, oc_newcolumn: `${base} (${short})` };
}

async function upsertLock(dataverseApiCall, lock) {
  const id = formatDataverseRecordKey(lock.id);
  let body = mapUnlocLockToDataverse(lock);
  const path = `/oc_locks(${id})`;

  const tryPatch = async (payload) => {
    await dataverseApiCall(path, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    });
  };

  const tryCreate = async (payload) => {
    await dataverseApiCall('/oc_locks', {
      method: 'POST',
      body: JSON.stringify({ oc_lockid: lock.id, ...payload })
    });
  };

  try {
    await tryPatch(body);
    return 'updated';
  } catch (err) {
    if (isUniqueConstraintError(err)) {
      body = withUniqueNewcolumn(body, lock.id);
      await tryPatch(body);
      return 'updated';
    }
    if (!isMissingRecordError(err)) {
      throw err;
    }
  }

  try {
    await tryCreate(body);
    return 'created';
  } catch (err) {
    if (!isUniqueConstraintError(err)) {
      throw err;
    }
    body = withUniqueNewcolumn(body, lock.id);
    await tryCreate(body);
    return 'created';
  }
}

function lockCoords(lock) {
  const lat = lock?.location?.latitude ?? lock?.locationGuess?.latitude;
  const lng = lock?.location?.longitude ?? lock?.locationGuess?.longitude;
  const latitude = typeof lat === 'number' ? lat : lat != null && lat !== '' ? Number(lat) : null;
  const longitude = typeof lng === 'number' ? lng : lng != null && lng !== '' ? Number(lng) : null;
  return {
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null
  };
}

/**
 * Sync Unloc locks into Dataverse from the browser (no Power Automate).
 * @param {Function} dataverseApiCall
 * @param {{ reconcileLocksInMatchingGroups?: Function } | null} accessGroupService
 */
export function createLockSyncService(dataverseApiCall, accessGroupService = null) {
  return {
    syncFromUnloc: async ({ onProgress } = {}) => {
      const report = ( partial ) => {
        onProgress?.(partial);
      };

      report({ phase: 'fetch', message: 'Henter låser fra Unloc…' });
      const unloc = await listAllLocks();
      if (!unloc) {
        throw new Error('Kunne ikke hente låser fra Unloc (mangler token eller tilgang).');
      }
      const locks = unloc.locks || [];
      report({
        phase: 'fetch',
        message: `Fant ${locks.length} låser i Unloc. Sammenligner med Dataverse…`,
        locksCount: locks.length
      });

      const existing = await listAllExistingUnlocLocks(dataverseApiCall);
      const toUpsert = [];
      let skipped = 0;
      for (const lock of locks) {
        if (!lock?.id) {
          continue;
        }
        const prev = existing.get(String(lock.id).toLowerCase());
        if (prev !== undefined && shouldSkip(lock, prev)) {
          skipped += 1;
        } else {
          toUpsert.push(lock);
        }
      }

      let upserted = 0;
      let failed = 0;
      const errors = [];

      report({
        phase: 'upsert',
        message: `Oppdaterer ${toUpsert.length} låser (${skipped} uendret)…`,
        locksCount: locks.length,
        upserted,
        skipped,
        failed
      });

      await mapPool(toUpsert, CONCURRENCY, async (lock) => {
        try {
          await upsertLock(dataverseApiCall, lock);
          upserted += 1;
        } catch (err) {
          failed += 1;
          errors.push(`${lock.name || lock.id}: ${err.message || err}`);
        }
        if ((upserted + failed) % 10 === 0 || upserted + failed === toUpsert.length) {
          report({
            phase: 'upsert',
            message: `Oppdaterer låser… ${upserted + failed}/${toUpsert.length}`,
            locksCount: locks.length,
            upserted,
            skipped,
            failed
          });
        }
      });

      let deactivated = 0;
      if (locks.length > 0) {
        report({
          phase: 'orphans',
          message: 'Deaktiverer låser som ikke finnes i Unloc…',
          locksCount: locks.length,
          upserted,
          skipped,
          failed
        });
        const unlocIds = new Set(locks.map((l) => String(l.id).toLowerCase()));
        const orphans = [...existing.keys()].filter((id) => !unlocIds.has(id));
        await mapPool(orphans, CONCURRENCY, async (id) => {
          try {
            await dataverseApiCall(`/oc_locks(${formatDataverseRecordKey(id)})`, {
              method: 'PATCH',
              body: JSON.stringify({ statecode: 1, statuscode: 2 })
            });
            deactivated += 1;
          } catch (err) {
            failed += 1;
            errors.push(`deaktiver ${id}: ${err.message || err}`);
          }
        });
      }

      let enrolled = 0;
      if (locks.length > 0 && accessGroupService?.reconcileLocksInMatchingGroups) {
        report({
          phase: 'enroll',
          message: 'Legger låser i tilgangsgrupper (default + kart-sone)…',
          locksCount: locks.length,
          upserted,
          skipped,
          failed,
          deactivated
        });
        try {
          const enrollResult = await accessGroupService.reconcileLocksInMatchingGroups(
            locks.map((lock) => ({
              id: lock.id,
              externalId: lock.id,
              name: lock.name,
              ...lockCoords(lock)
            }))
          );
          enrolled = enrollResult?.associations || 0;
          for (const enrollError of enrollResult?.errors || []) {
            errors.push(`tilgangsgruppe: ${enrollError}`);
          }
        } catch (enrollError) {
          console.warn('Access group enroll during lock sync failed:', enrollError);
          errors.push(`tilgangsgruppe: ${enrollError.message || enrollError}`);
        }
      }

      if (failed > 0 && upserted === 0 && deactivated === 0) {
        throw new Error(errors[0] || 'Lås-synk feilet.');
      }

      return {
        locksCount: locks.length,
        upserted,
        skipped,
        failed,
        deactivated,
        enrolled,
        errors
      };
    }
  };
}
